import RefreshButton from '@casops/components/inputs/refresh-button/RefreshButton';
import { ModelName, QueryTag, TagList } from '@casops/store/api';
import {
  ConfigurationGroupModel,
  useDeleteConfigurationGroupMutation,
  useGetConfigurationGroupQuery,
} from '@casops/store/apis/configurationGroupsApi';
import { skipToken, TagDescription } from '@reduxjs/toolkit/query';
import { ReactElement, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import styles from './ConfigurationGroupHeader.module.scss';
import { useCompatibleDevicesLabels } from '@casops/components/tables/cells/compatible-devices/CompatibleDevicesCellRenderer';
import { useCompatibleSoftwareLabels } from '@casops/components/tables/cells/compatible-software/CompatibleSoftwareCellRenderer';
import { VdsChip } from '@volt/volt-extended-components-react';
import EditItemButton from '@casops/components/inputs/edit-item-button/EditItemButton';
import { SidePanel } from '@casops/store/slices/globalSlice';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { useTranslation } from 'react-i18next';
import DeleteAction from '@casops/components/tables/cells/model-actions/components/delete-action/DeleteAction';
import { getDefaultModelArg } from '@casops/components/tables/cells/model-actions/utils';
import DeleteDialogButton from '@casops/dialogs/delete-model-dialog/components/button/DeleteDialogButton';

type Model = ConfigurationGroupModel;

const modelName = ModelName.ConfigurationGroup;
const sidePanel = SidePanel.ManageConfigurationGroup;
const permission = Permission.Admin;

const getItemName = (data: Model): string => data.name;

interface ConfigurationGroupHeaderProps {
  modelId?: number;
}

export const ConfigurationGroupHeader = ({
  modelId,
}: ConfigurationGroupHeaderProps): ReactElement => {
  const { t } = useTranslation();

  const tagsToClear = useMemo<TagDescription<QueryTag>[]>(
    () => [
      { type: QueryTag.ConfigurationGroup, id: modelId },
      { type: QueryTag.AssetRegistered, id: TagList }, // Assets tab.
    ],
    [modelId],
  );

  const queryArgs = typeof modelId !== 'undefined' ? { id: modelId } : skipToken;

  const { data: configGroup, isError } = useGetConfigurationGroupQuery(queryArgs, {
    refetchOnMountOrArgChange: 60,
  });

  const { name, desc, hw } = configGroup ?? {};

  // Use the hardware type to get the relevant device and software translations.
  const compatibleDeviceLabels = useCompatibleDevicesLabels(hw, true);
  const compatibleSoftwareLabels = useCompatibleSoftwareLabels(hw, true);

  // Only show the actions if the user has the required permission.
  const showActions = useIsUserWithPermission(permission);

  // If there is a fetching error or the data is not loaded, then disable actions.
  const disableActions = isError || typeof configGroup === 'undefined';

  return (
    <header className={styles.header}>
      <div className={styles.titleBlock}>
        <div className={styles.name}>{name ?? <Skeleton width={250} height={32} />}</div>
        <div className={styles.chips}>
          {[...compatibleSoftwareLabels, ...compatibleDeviceLabels].map(
            (label: string, id: number) => {
              // The label is not unique so it is not a suitable key.
              return <VdsChip key={id} active={false} value={label} />;
            },
          )}
        </div>
      </div>

      <div className={styles.actions}>
        {showActions && (
          <>
            <EditItemButton
              modelId={modelId}
              modelName={modelName}
              sidePanel={sidePanel}
              permission={permission}
              label={t('Edit')}
              // Disable while loading for consistency with the delete button.
              disabled={disableActions}
            />

            {!disableActions ? (
              <DeleteAction<Model>
                modelName={modelName}
                data={configGroup}
                getItemName={getItemName}
                getQueryArg={getDefaultModelArg}
                useDeleteModelMutation={useDeleteConfigurationGroupMutation}
                canDeleteItem={!configGroup.used}
                isIconButton={false}
                navigateTo="/configuration/configuration-groups"
              />
            ) : (
              // Display a function-less disabled button to preserve layout while loading data.
              <DeleteDialogButton isIconButton={false} disabled={true} />
            )}
          </>
        )}
        <RefreshButton tagsToClear={tagsToClear} />
      </div>

      {desc && <div className={styles.description}>{desc}</div>}
    </header>
  );
};

export default ConfigurationGroupHeader;
